/* eslint-disable max-len */
import React, { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';
import '../changeRequest.scss';

// Helper component: Only shows tooltip if text is actually truncated (...)
const OverflowTooltipText = ({ text, className }) => {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const handleMouseEnter = () => {
    if (textRef.current) {
      // Check if the text is wider than its container
      setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  };

  return (
    <div
      ref={textRef}
      className={className}
      title={isOverflowing ? text : undefined}
      onMouseEnter={handleMouseEnter}
    >
      {text}
    </div>
  );
};

const Row = ({ label, fromValue, toValue }) => {
  const displayFrom = fromValue ?? 'No Value';
  const displayTo = toValue === 'No Changes' ? '-' : (toValue ?? '');

  return (
    <div className="cr-details-row">
      <OverflowTooltipText className="cr-details-field" text={label} />
      <OverflowTooltipText className="cr-details-from" text={displayFrom} />
      <div className="cr-details-arrow">&rarr;</div>
      <OverflowTooltipText className="cr-details-to" text={displayTo} />
    </div>
  );
};

const EffortRow = ({ item }) => {
  const category = item.resourceCategory || item.resource_category;
  const skills = item.resourceSkills || item.resource_skills || item.resource_type;
  const effort = item.effortHrs || item.efforts_hours;
  const details = item.details;

  return (
    <tr>
      <td><OverflowTooltipText text={category} /></td>
      <td><OverflowTooltipText text={skills} /></td>
      <td className="right-align"><OverflowTooltipText text={effort} /></td>
      <td><OverflowTooltipText text={details} /></td>
    </tr>
  );
};

const CRDetailsDialog = ({ visible, onHide, data }) => {
  const diff = data?.diff || {};
  const effortRows = data?.effortRows || data?.effort_rows || data?.backup?.effort_rows || [];

  // Use the pre-existing total efforts value from your data
  const finalTotalEfforts = diff?.totalEfforts?.from || data?.backup?.effort_hour || data?.current?.efforts_hour || 0;

  return (
    <Dialog
      header="CR Details"
      maskClassName="cr-details-dialog-mask"
      visible={visible}
      onHide={onHide}
      modal
      className="cr-edit-dialog"
    >
      <div className="cr-details-grid">
        <div className="cr-details-scrollable-content">

          <div className="cr-details-section">
            <div className="cr-details-table-wrapper">
              <div className="cr-details-table">
                <div className="cr-details-header">
                  <div className="cr-header-labels">Fields</div>
                  <div className="cr-header-labels">Changed From</div>
                  <div className="cr-arrow-div" /> {/* Empty cell to maintain grid alignment */}
                  <div className="cr-header-labels">Changed To</div>
                </div>
                <Row label="CR Status" fromValue={diff?.crStatus?.from} toValue={diff?.crStatus?.to} />
                <Row label="CR Name" fromValue={diff?.crName?.from || data?.backup?.account_name || data?.current?.account_name} toValue={diff?.crName?.to} />
                <Row label="CR Description" fromValue={diff?.crDescription?.from || data?.backup?.cr_description || data?.current?.description} toValue={diff?.crDescription?.to} />
                <Row label="Efforts Investment" fromValue={diff?.effortsInvestment?.from || data?.backup?.effort_investment} toValue={diff?.effortsInvestment?.to} />
                <Row label="Customer Approval Status" fromValue={diff?.customerApprovalStatus?.from || data?.backup?.customer_ap_value} toValue={diff?.customerApprovalStatus?.to} />
                <Row label="Total Efforts" fromValue={diff?.totalEfforts?.from || data?.current?.efforts_hour} toValue={diff?.totalEfforts?.to || data?.backup?.effort_hour} />
                <Row label="Remarks" fromValue={diff?.remarks?.from || data?.backup?.remarks || data?.current?.remarks} toValue={diff?.remarks?.to} />
              </div>
            </div>
          </div>

          <div className="cr-effort-section">
            <h4>Effort Estimation</h4>

            <div className="cr-effort-table-wrapper">
              <table className="cr-effort-table">
                <thead>
                  <tr>
                    <th>Resource Category</th>
                    <th>Resource Skills</th>
                    <th>Effort (Hrs)</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(effortRows) && effortRows.length > 0 ? (
                    effortRows.map((it, idx) => <EffortRow key={it.id || idx} item={it} />)
                  ) : (
                    <tr>
                      <td colSpan={4} className="cr-empty-table">
                        No effort rows
                      </td>
                    </tr>
                  )}
                </tbody>

                {/* Total Row Footer */}
                {Array.isArray(effortRows) && effortRows.length > 0 && (
                  <tfoot>
                    <tr className="cr-effort-total-row">
                      {/* Col 1: Resource Category */}
                      <td className="cr-effort-total-cell"></td>

                      {/* Col 2: Resource Skills */}
                      <td className="cr-effort-total-cell"></td>

                      {/* Col 3: Effort (Hrs) */}
                      <td className="cr-effort-total-cell">
                        <div className="total-cell">
                          <span className="total-label">Total:</span>
                          <span className="total-value">{finalTotalEfforts}</span>
                        </div>
                      </td>

                      {/* Col 4: Details */}
                      <td className="cr-effort-total-cell"></td>
                    </tr>
                  </tfoot>
                )}

              </table>
            </div>
          </div>
        </div>x``

        {/* Pinned Sticky Footer */}
        <div className="cr-estimation-dialog__footer">
          <div className="cr-estimation-dialog__footer-button-wrapper">
            <button
              type="button"
              className="cr-estimation-dialog__btn cr-estimation-dialog__btn--cancel"
              onClick={onHide}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

OverflowTooltipText.propTypes = {
  text: PropTypes.any,
  className: PropTypes.string,
};

Row.propTypes = {
  label: PropTypes.string.isRequired,
  fromValue: PropTypes.any,
  toValue: PropTypes.any,
};

EffortRow.propTypes = {
  item: PropTypes.shape({
    resourceCategory: PropTypes.any,
    resource_category: PropTypes.any,
    resourceSkills: PropTypes.any,
    resource_skills: PropTypes.any,
    resource_type: PropTypes.any,
    effortHrs: PropTypes.any,
    efforts_hours: PropTypes.any,
    details: PropTypes.any,
  }).isRequired,
};

CRDetailsDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  data: PropTypes.object,
};

export default CRDetailsDialog;
